import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, Search, X } from 'lucide-react'

export interface SearchableOption {
  value: string
  label: string
  /** Secondary line: plate, customer, corridor length — whatever disambiguates. */
  hint?: string | null
}

interface SearchableSelectProps {
  options: SearchableOption[]
  value: string
  onChange: (value: string) => void
  placeholder: string
  searchPlaceholder?: string
  emptyText?: string
  clearText?: string
  disabled?: boolean
  className?: string
}

/**
 * A select you can type into. The fleet lists (vehicles, drivers, corridors,
 * customers) are far too long for a native dropdown, which offers no way to
 * search and forces scrolling through hundreds of entries.
 *
 * Deliberately dependency-free: the inline filter patterns that already exist in
 * FleetGroupsPanel and MissionCreator are copies of the same idea, and this is
 * the shared version of it.
 */
const SearchableSelect: React.FC<SearchableSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  clearText,
  disabled = false,
  className = ''
}) => {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const selected = options.find(option => option.value === value)

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return options
    return options.filter(option =>
      option.label.toLowerCase().includes(needle) ||
      (option.hint || '').toLowerCase().includes(needle))
  }, [options, query])

  // Clicking anywhere else closes the popover; without this the list stays open
  // behind the next control the user reaches for.
  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open])

  useEffect(() => {
    if (open) {
      setQuery('')
      setHighlighted(0)
      // Focus lands in the filter box so typing works straight away.
      const timer = window.setTimeout(() => searchRef.current?.focus(), 10)
      return () => window.clearTimeout(timer)
    }
  }, [open])

  const commit = (option: SearchableOption) => {
    onChange(option.value)
    setOpen(false)
  }

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      setOpen(false)
      return
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setHighlighted(current => {
        const next = event.key === 'ArrowDown' ? current + 1 : current - 1
        if (next < 0) return Math.max(filtered.length - 1, 0)
        return next >= filtered.length ? 0 : next
      })
      return
    }
    if (event.key === 'Enter' && filtered[highlighted]) {
      event.preventDefault()
      commit(filtered[highlighted])
    }
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(current => !current)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border px-3 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
          disabled ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400' : 'border-gray-300 bg-white text-gray-800'
        }`}
      >
        <span className={`truncate ${selected ? '' : 'text-gray-400'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronDown size={14} className="flex-shrink-0 text-gray-400" />
      </button>

      {open && !disabled && (
        <div className="absolute left-0 right-0 z-30 mt-1 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center gap-2 border-b border-gray-100 px-2.5 py-2">
            <Search size={13} className="flex-shrink-0 text-gray-400" />
            <input
              ref={searchRef}
              value={query}
              onChange={event => { setQuery(event.target.value); setHighlighted(0) }}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder || placeholder}
              className="w-full border-0 p-0 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-0"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="text-gray-400 hover:text-gray-600">
                <X size={13} />
              </button>
            )}
          </div>

          <div className="max-h-60 overflow-y-auto py-1">
            {clearText && (
              <button
                type="button"
                onClick={() => { onChange(''); setOpen(false) }}
                className="w-full px-3 py-1.5 text-left text-xs italic text-gray-500 hover:bg-gray-50"
              >
                {clearText}
              </button>
            )}
            {filtered.length === 0 ? (
              <p className="px-3 py-3 text-center text-xs italic text-gray-500">
                {emptyText || '—'}
              </p>
            ) : filtered.map((option, index) => (
              <button
                key={option.value}
                type="button"
                onMouseEnter={() => setHighlighted(index)}
                onClick={() => commit(option)}
                className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left ${
                  index === highlighted ? 'bg-blue-50' : ''
                }`}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm text-gray-800">{option.label}</span>
                  {option.hint && (
                    <span className="block truncate text-[11px] text-gray-400">{option.hint}</span>
                  )}
                </span>
                {option.value === value && <Check size={13} className="flex-shrink-0 text-blue-600" />}
              </button>
            ))}
          </div>

          {options.length > 12 && (
            <p className="border-t border-gray-100 px-3 py-1.5 text-[10px] text-gray-400">
              {filtered.length} / {options.length}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

export default SearchableSelect
